import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {
    X, Plus, Code, Trash2, ArrowDown, ArrowUp, CheckSquare, Square,
    Copy, HelpCircle, AlertCircle, Info, ChevronDown, ChevronRight, FileText,
    AlertTriangle, CopyCheck, Download, RefreshCw, Upload, Clipboard, Save,
    CheckCircle, Edit, PlayCircle, StopCircle, PlusSquare, RotateCcw, Layers
} from 'lucide-react';

// Helper to create a unique ID
const createId = () => `id_${Math.random().toString(36).substring(2, 11)}`;

// --- MODIFIED CODE FOR NESTED RULES ---

// Create a new empty rule structure that can have subrules
const createEmptyRule = (isSubrule = false) => ({
    id: createId(),
    active: true,
    conditions: [{
        type: 'header',
        comparator: 'i;ascii-casemap',
        matchType: ':is',
        header: 'Subject',
        keys: ['']
    }],
    actions: isSubrule ? [{type: 'fileinto', parameters: {mailbox: 'INBOX.Filtered'}}] : [],
    subrules: [],
    combinator: 'anyof',
    isSubrule: isSubrule
});

// Modified script templates to include subrules
const SCRIPT_TEMPLATES = {
    // ...existing templates would go here...

    // Add a new template with nested rules
    nestedDomainRules: {
        name: "Domain-specific Rules",
        rules: [
            {
                id: createId(),
                active: true,
                conditions: [{
                    type: 'address',
                    comparator: 'i;ascii-casemap',
                    matchType: ':contains',
                    addressPart: ':domain',
                    header: 'From',
                    keys: ['company.com']
                }],
                actions: [], // No direct actions, using subrules instead
                subrules: [
                    {
                        id: createId(),
                        active: true,
                        isSubrule: true,
                        conditions: [{
                            type: 'header',
                            comparator: 'i;ascii-casemap',
                            matchType: ':contains',
                            header: 'Subject',
                            keys: ['Urgent']
                        }],
                        actions: [{type: 'fileinto', parameters: {mailbox: 'INBOX.Company.Urgent'}}],
                        subrules: [],
                        combinator: 'anyof'
                    },
                    {
                        id: createId(),
                        active: true,
                        isSubrule: true,
                        conditions: [{
                            type: 'header',
                            comparator: 'i;ascii-casemap',
                            matchType: ':contains',
                            header: 'Subject',
                            keys: ['Newsletter']
                        }],
                        actions: [{type: 'fileinto', parameters: {mailbox: 'INBOX.Company.Newsletter'}}],
                        subrules: [],
                        combinator: 'anyof'
                    }
                ],
                combinator: 'anyof'
            }
        ],
        extensions: {fileinto: true}
    }
};

// Rest of the extension categories, descriptions, and help text would go here...

// Enhanced Sieve parser for importing scripts (would need to be updated for nested rules)
const parseSieveScript = (script) => {
    // This would need significant updates to handle nested if blocks
    // Basic implementation here...
    try {
        // ... existing parsing logic ...

        // Would need to detect nested if blocks and create appropriate rule structures

        return {
            rules: [],
            extensions: {}
        };
    } catch (e) {
        console.error("Error parsing Sieve script:", e);
        return null;
    }
};

// Define the main editor component
const SieveEditor = () => {
    // State for rules
    const [rules, setRules] = useState([]);
    // State for extensions
    const [extensions, setExtensions] = useState({
        fileinto: true,
        reject: false,
        vacation: false,
        envelope: false,
        variables: false,
        body: false,
        relational: false,
        comparator: false,
        subaddress: false,
        copy: false,
        imap4flags: false,
        date: false,
        index: false,
        editheader: false,
        enotify: false,
        environment: false,
        mime: false,
        convert: false,
        include: false,
        fcc: false
    });
    // Other state variables (code, warnings, UI states)
    const [generatedCode, setGeneratedCode] = useState('');
    const [showCode, setShowCode] = useState(false);
    const [validationWarnings, setValidationWarnings] = useState([]);
    const [copied, setCopied] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({
        core: true,
        actions: false,
        tests: false,
        message: false,
        advanced: false
    });
    const [showHelp, setShowHelp] = useState(false);
    const [notification, setNotification] = useState(null);
    const [codeEditMode, setCodeEditMode] = useState(false);
    const [editedCode, setEditedCode] = useState('');

    // State for expanded rules in UI
    const [expandedRules, setExpandedRules] = useState({});

    // Refs
    const downloadLinkRef = useRef(null);
    const importFileRef = useRef(null);
    const codeEditorRef = useRef(null);

    // Initial setup & effects
    useEffect(() => {
        if (!codeEditMode) {
            generateCode();
        }
    }, [rules, extensions, codeEditMode]);

    useEffect(() => {
        const savedScript = localStorage.getItem('sieve-script');
        if (savedScript) {
            try {
                const {rules: savedRules, extensions: savedExtensions} = JSON.parse(savedScript);
                if (savedRules && Array.isArray(savedRules) && savedRules.length > 0) {
                    setRules(savedRules);

                    // Initialize expanded state for all rules
                    const expandedState = {};
                    const initExpandState = (ruleList) => {
                        ruleList.forEach(rule => {
                            expandedState[rule.id] = true;
                            if (rule.subrules?.length) {
                                initExpandState(rule.subrules);
                            }
                        });
                    };
                    initExpandState(savedRules);
                    setExpandedRules(expandedState);
                } else {
                    // Start with a basic rule if no saved script
                    addRule();
                }
                if (savedExtensions && typeof savedExtensions === 'object') {
                    setExtensions(savedExtensions);
                }
            } catch (e) {
                console.error("Error loading saved script:", e);
                // Start with a basic rule if loading fails
                addRule();
                showNotification("Error loading saved script. Starting with a new script.", "error");
            }
        } else {
            // Start with a basic rule if no saved script
            addRule();
        }
    }, []);

    useEffect(() => {
        if (rules.length > 0) {
            localStorage.setItem('sieve-script', JSON.stringify({
                rules,
                extensions
            }));
        }
    }, [rules, extensions]);

    useEffect(() => {
        validateScript();
    }, [rules, extensions]);

    // Show a notification message
    const showNotification = useCallback((message, type = "info") => {
        setNotification({message, type});
        setTimeout(() => setNotification(null), 3000);
    }, []);

    // Toggle expanded state for a rule
    const toggleRuleExpanded = useCallback((ruleId) => {
        setExpandedRules(prev => ({
            ...prev,
            [ruleId]: !prev[ruleId]
        }));
    }, []);

    // Add a new top-level rule
    const addRule = useCallback(() => {
        setRules(prevRules => {
            const newRule = createEmptyRule();
            setExpandedRules(prev => ({
                ...prev,
                [newRule.id]: true
            }));
            return [...prevRules, newRule];
        });
    }, []);

    // Add a subrule to a parent rule
    const addSubrule = useCallback((parentRuleId) => {
        setRules(prevRules => {
            // Function to recursively find and update a rule
            const updateRuleWithSubrule = (rulesList, parentId) => {
                return rulesList.map(rule => {
                    if (rule.id === parentId) {
                        const newSubrule = createEmptyRule(true);
                        setExpandedRules(prev => ({
                            ...prev,
                            [newSubrule.id]: true
                        }));
                        return {
                            ...rule,
                            subrules: [...(rule.subrules || []), newSubrule]
                        };
                    }
                    if (rule.subrules?.length) {
                        return {
                            ...rule,
                            subrules: updateRuleWithSubrule(rule.subrules, parentId)
                        };
                    }
                    return rule;
                });
            };

            return updateRuleWithSubrule(prevRules, parentRuleId);
        });
        showNotification("Added nested rule", "success");
    }, [showNotification]);

    // Remove a rule (top-level or subrule)
    const removeRule = useCallback((ruleId, parentId = null) => {
        if (!parentId) {
            // Top-level rule
            setRules(prevRules => prevRules.filter(rule => rule.id !== ruleId));
        } else {
            // Subrule - need to find parent and update its subrules
            setRules(prevRules => {
                // Function to recursively find and update a rule
                const updateRulesByRemovingSubrule = (rulesList, parentId, ruleIdToRemove) => {
                    return rulesList.map(rule => {
                        if (rule.id === parentId) {
                            return {
                                ...rule,
                                subrules: rule.subrules.filter(subrule => subrule.id !== ruleIdToRemove)
                            };
                        }
                        if (rule.subrules?.length) {
                            return {
                                ...rule,
                                subrules: updateRulesByRemovingSubrule(rule.subrules, parentId, ruleIdToRemove)
                            };
                        }
                        return rule;
                    });
                };

                return updateRulesByRemovingSubrule(prevRules, parentId, ruleId);
            });
        }
        showNotification("Rule removed", "info");
    }, [showNotification]);

    // Toggle rule activation
    const toggleRuleActive = useCallback((ruleId) => {
        const updateRulesActive = (rulesList, targetId) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    return {...rule, active: !rule.active};
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesActive(rule.subrules, targetId)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesActive(prevRules, ruleId));
    }, []);

    // Modify condition-related functions to work with nested rules
    const addCondition = useCallback((ruleId) => {
        const updateRulesWithCondition = (rulesList, targetId) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    return {
                        ...rule,
                        conditions: [
                            ...rule.conditions,
                            {type: 'header', comparator: 'i;ascii-casemap', matchType: ':is', header: 'Subject', keys: ['']}
                        ]
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesWithCondition(rule.subrules, targetId)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesWithCondition(prevRules, ruleId));
    }, []);

    const removeCondition = useCallback((ruleId, index) => {
        const updateRulesRemoveCondition = (rulesList, targetId, condIndex) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    const newConditions = [...rule.conditions];
                    newConditions.splice(condIndex, 1);
                    return {
                        ...rule,
                        conditions: newConditions
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesRemoveCondition(rule.subrules, targetId, condIndex)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesRemoveCondition(prevRules, ruleId, index));
    }, []);

    const updateCondition = useCallback((ruleId, index, updates) => {
        const updateRulesCondition = (rulesList, targetId, condIndex, condUpdates) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    const newConditions = [...rule.conditions];
                    newConditions[condIndex] = {...newConditions[condIndex], ...condUpdates};
                    return {
                        ...rule,
                        conditions: newConditions
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesCondition(rule.subrules, targetId, condIndex, condUpdates)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesCondition(prevRules, ruleId, index, updates));
    }, []);

    // Modify action-related functions to work with nested rules
    const addAction = useCallback((ruleId) => {
        const updateRulesWithAction = (rulesList, targetId) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    return {
                        ...rule,
                        actions: [
                            ...rule.actions,
                            {type: 'fileinto', parameters: {mailbox: 'INBOX.Filtered'}}
                        ]
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesWithAction(rule.subrules, targetId)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesWithAction(prevRules, ruleId));
    }, []);

    const removeAction = useCallback((ruleId, index) => {
        const updateRulesRemoveAction = (rulesList, targetId, actionIndex) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    const newActions = [...rule.actions];
                    newActions.splice(actionIndex, 1);
                    return {
                        ...rule,
                        actions: newActions
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesRemoveAction(rule.subrules, targetId, actionIndex)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesRemoveAction(prevRules, ruleId, index));
    }, []);

    const updateAction = useCallback((ruleId, index, updates) => {
        const updateRulesAction = (rulesList, targetId, actionIndex, actionUpdates) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    const newActions = [...rule.actions];
                    newActions[actionIndex] = {...newActions[actionIndex], ...actionUpdates};
                    return {
                        ...rule,
                        actions: newActions
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesAction(rule.subrules, targetId, actionIndex, actionUpdates)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesAction(prevRules, ruleId, index, updates));
    }, []);

    // Toggle combinator between anyof and allof
    const toggleCombinator = useCallback((ruleId) => {
        const updateRulesCombinator = (rulesList, targetId) => {
            return rulesList.map(rule => {
                if (rule.id === targetId) {
                    return {
                        ...rule,
                        combinator: rule.combinator === 'anyof' ? 'allof' : 'anyof'
                    };
                }
                if (rule.subrules?.length) {
                    return {
                        ...rule,
                        subrules: updateRulesCombinator(rule.subrules, targetId)
                    };
                }
                return rule;
            });
        };

        setRules(prevRules => updateRulesCombinator(prevRules, ruleId));
    }, []);

    // Move rules up or down (simplified, would need to be updated for nested rules)
    const moveRuleUp = useCallback((index) => {
        if (index === 0) return;
        setRules(prevRules => {
            const newRules = [...prevRules];
            const temp = newRules[index];
            newRules[index] = newRules[index - 1];
            newRules[index - 1] = temp;
            return newRules;
        });
    }, []);

    const moveRuleDown = useCallback((index) => {
        setRules(prevRules => {
            if (index === prevRules.length - 1) return prevRules;
            const newRules = [...prevRules];
            const temp = newRules[index];
            newRules[index] = newRules[index + 1];
            newRules[index + 1] = temp;
            return newRules;
        });
    }, []);

    // Duplicate rule function (would need to be updated for nested rules)
    const duplicateRule = useCallback((ruleId, parentId = null) => {
        if (!parentId) {
            // Top-level rule duplication
            setRules(prevRules => {
                const ruleToDuplicate = prevRules.find(rule => rule.id === ruleId);
                if (!ruleToDuplicate) return prevRules;

                // Create a deep copy with new IDs
                const duplicatedRule = JSON.parse(JSON.stringify(ruleToDuplicate));

                // Function to recursively assign new IDs
                const assignNewIds = (rule) => {
                    const newId = createId();
                    rule.id = newId;
                    setExpandedRules(prev => ({
                        ...prev,
                        [newId]: true
                    }));

                    if (rule.subrules?.length) {
                        rule.subrules.forEach(subrule => assignNewIds(subrule));
                    }
                    return rule;
                };

                assignNewIds(duplicatedRule);

                // Find the index of the original rule
                const index = prevRules.findIndex(rule => rule.id === ruleId);

                // Insert the duplicated rule after the original
                const newRules = [...prevRules];
                newRules.splice(index + 1, 0, duplicatedRule);

                showNotification("Rule duplicated", "success");
                return newRules;
            });
        } else {
            // Subrule duplication
            setRules(prevRules => {
                // Function to find and duplicate a subrule
                const duplicateSubrule = (rulesList, parentId, subruleId) => {
                    return rulesList.map(rule => {
                        if (rule.id === parentId) {
                            const subruleIndex = rule.subrules.findIndex(sr => sr.id === subruleId);
                            if (subruleIndex === -1) return rule;

                            // Deep copy the subrule
                            const duplicatedSubrule = JSON.parse(JSON.stringify(rule.subrules[subruleIndex]));

                            // Assign new IDs
                            const assignNewIds = (subrule) => {
                                const newId = createId();
                                subrule.id = newId;
                                setExpandedRules(prev => ({
                                    ...prev,
                                    [newId]: true
                                }));

                                if (subrule.subrules?.length) {
                                    subrule.subrules.forEach(nestedSubrule => assignNewIds(nestedSubrule));
                                }
                                return subrule;
                            };

                            assignNewIds(duplicatedSubrule);

                            // Insert the duplicate
                            const newSubrules = [...rule.subrules];
                            newSubrules.splice(subruleIndex + 1, 0, duplicatedSubrule);

                            return {
                                ...rule,
                                subrules: newSubrules
                            };
                        }

                        if (rule.subrules?.length) {
                            return {
                                ...rule,
                                subrules: duplicateSubrule(rule.subrules, parentId, subruleId)
                            };
                        }

                        return rule;
                    });
                };

                return duplicateSubrule(prevRules, parentId, ruleId);
            });

            showNotification("Subrule duplicated", "success");
        }
    }, [showNotification]);

    // Validate script - check for issues in the nested structure
    const validateScript = useCallback(() => {
        const warnings = [];

        // Recursive function to check all rules and subrules
        const validateRules = (rulesList, path = '') => {
            rulesList.forEach((rule, index) => {
                const rulePath = path ? `${path} > Rule ${index + 1}` : `Rule ${index + 1}`;

                if (rule.active) {
                    // Check conditions
                    if (rule.conditions.length === 0) {
                        warnings.push(`${rulePath} has no conditions`);
                    } else {
                        // Check for incomplete conditions
                        rule.conditions.forEach((condition, condIndex) => {
                            if (condition.type === 'header' && (!condition.header || condition.header.trim() === '')) {
                                warnings.push(`${rulePath}, condition ${condIndex + 1} has an empty header name`);
                            }
                            if ((condition.type === 'header' || condition.type === 'address') &&
                                Array.isArray(condition.keys) &&
                                (condition.keys.length === 0 || condition.keys[0] === '')) {
                                warnings.push(`${rulePath}, condition ${condIndex + 1} has an empty value`);
                            }
                        });
                    }

                    // For parent rules with subrules, we allow empty actions
                    // For leaf rules (no subrules), we require actions
                    if (rule.subrules?.length === 0 && rule.actions.length === 0) {
                        warnings.push(`${rulePath} has no actions and no subrules`);
                    }

                    // Check for incomplete actions
                    rule.actions.forEach((action, actionIndex) => {
                        if (action.type === 'fileinto' && (!action.parameters?.mailbox || action.parameters.mailbox.trim() === '')) {
                            warnings.push(`${rulePath}, action ${actionIndex + 1} has an empty mailbox name`);
                        }
                        if (action.type === 'redirect' && (!action.parameters?.address || action.parameters.address.trim() === '')) {
                            warnings.push(`${rulePath}, action ${actionIndex + 1} has an empty redirect address`);
                        }
                    });

                    // Check subrules recursively
                    if (rule.subrules?.length > 0) {
                        validateRules(rule.subrules, rulePath);
                    }
                }
            });
        };

        // Check if any rule is active
        const hasActiveRule = (rulesList) => {
            return rulesList.some(rule => {
                if (rule.active) return true;
                if (rule.subrules?.length) return hasActiveRule(rule.subrules);
                return false;
            });
        };

        if (rules.length > 0 && !hasActiveRule(rules)) {
            warnings.push("No active rules - script will have no effect");
        }

        // Start the recursive validation
        validateRules(rules);

        // Check for required extensions
        const usedActions = new Set();
        const usedTests = new Set();

        const collectUsedFeatures = (rulesList) => {
            rulesList.forEach(rule => {
                if (rule.active) {
                    rule.actions.forEach(action => usedActions.add(action.type));
                    rule.conditions.forEach(condition => usedTests.add(condition.type));
                    if (rule.subrules?.length) collectUsedFeatures(rule.subrules);
                }
            });
        };

        collectUsedFeatures(rules);

        // Check if actions need extensions
        if (usedActions.has('fileinto') && !extensions.fileinto) {
            warnings.push("Script uses 'fileinto' action but 'fileinto' extension is not enabled");
        }
        if (usedActions.has('reject') && !extensions.reject) {
            warnings.push("Script uses 'reject' action but 'reject' extension is not enabled");
        }
        if (usedActions.has('vacation') && !extensions.vacation) {
            warnings.push("Script uses 'vacation' action but 'vacation' extension is not enabled");
        }
        if (usedTests.has('envelope') && !extensions.envelope) {
            warnings.push("Script uses 'envelope' test but 'envelope' extension is not enabled");
        }
        if (usedTests.has('body') && !extensions.body) {
            warnings.push("Script uses 'body' test but 'body' extension is not enabled");
        }

        setValidationWarnings(warnings);
    }, [rules, extensions]);

    // Toggle extension
    const toggleExtension = useCallback((ext) => {
        setExtensions(prevExtensions => ({
            ...prevExtensions,
            [ext]: !prevExtensions[ext]
        }));
    }, []);

    // Function to generate Sieve code from the rules
    const generateCode = useCallback(() => {
        let code = '';

        // Add require section first
        const requiredExtensions = [];
        if (extensions.fileinto) requiredExtensions.push('fileinto');
        if (extensions.reject) requiredExtensions.push('reject');
        if (extensions.vacation) requiredExtensions.push('vacation');
        if (extensions.envelope) requiredExtensions.push('envelope');
        if (extensions.variables) requiredExtensions.push('variables');
        if (extensions.body) requiredExtensions.push('body');
        if (extensions.relational) requiredExtensions.push('relational');
        if (extensions.comparator && requiredExtensions.indexOf('comparator-i;ascii-casemap') === -1)
            requiredExtensions.push('comparator-i;ascii-casemap');
        if (extensions.subaddress) requiredExtensions.push('subaddress');
        if (extensions.copy) requiredExtensions.push('copy');
        if (extensions.imap4flags) requiredExtensions.push('imap4flags');
        if (extensions.date) requiredExtensions.push('date');
        if (extensions.index) requiredExtensions.push('index');
        if (extensions.editheader) requiredExtensions.push('editheader');
        if (extensions.enotify) requiredExtensions.push('enotify');
        if (extensions.environment) requiredExtensions.push('environment');
        if (extensions.mime) requiredExtensions.push('mime');
        if (extensions.convert) requiredExtensions.push('convert');
        if (extensions.include) requiredExtensions.push('include');
        if (extensions.fcc) requiredExtensions.push('fcc');

        if (requiredExtensions.length > 0) {
            code += `require [${requiredExtensions.map(ext => `"${ext}"`).join(', ')}];\n\n`;
        }

        // Filter rules that are active
        const generateNestedRules = (rulesList, indent = 0) => {
            const activeRules = rulesList.filter(rule => rule.active);
            if (activeRules.length === 0) return '';

            let ruleCode = '';
            const indentStr = ' '.repeat(indent);

            activeRules.forEach((rule, index) => {
                // Start with an if or elsif
                if (indent === 0) {
                    // Top-level rules
                    if (index === 0) {
                        ruleCode += `${indentStr}if ${generateConditionCode(rule)}\n${indentStr}{\n`;
                    } else {
                        ruleCode += `${indentStr}elsif ${generateConditionCode(rule)}\n${indentStr}{\n`;
                    }
                } else {
                    // Nested rules need a condition to start
                    ruleCode += `${indentStr}if ${generateConditionCode(rule)}\n${indentStr}{\n`;
                }

                // Add rule actions
                rule.actions.forEach(action => {
                    ruleCode += `${indentStr}    ${generateActionCode(action)};\n`;
                });

                // Add subrules
                if (rule.subrules && rule.subrules.length > 0) {
                    ruleCode += generateNestedRules(rule.subrules, indent + 4);
                }

                // Close the block
                ruleCode += `${indentStr}}\n`;
            });

            return ruleCode;
        };

        code += generateNestedRules(rules);

        setGeneratedCode(code);
        if (codeEditMode) {
            setEditedCode(code);
        }
    }, [rules, extensions, codeEditMode]);

    // Helper to generate condition code for a rule
    const generateConditionCode = useCallback((rule) => {
        if (rule.conditions.length === 0) {
            return 'true';
        }

        if (rule.conditions.length === 1) {
            return generateSingleConditionCode(rule.conditions[0]);
        }

        // Multiple conditions using the combinator
        return `${rule.combinator} (${
            rule.conditions.map(condition => generateSingleConditionCode(condition)).join(', ')
        })`;
    }, []);

    // Helper to generate a single condition code
    const generateSingleConditionCode = useCallback((condition) => {
        switch (condition.type) {
            case 'header':
                return `header ${condition.matchType} ${condition.comparator !== 'i;ascii-casemap' ? `:comparator "${condition.comparator}" ` : ''}${JSON.stringify(condition.header)} ${JSON.stringify(condition.keys[0])}`;

            case 'address':
                return `address ${condition.matchType} ${condition.addressPart || ':all'} ${condition.comparator !== 'i;ascii-casemap' ? `:comparator "${condition.comparator}" ` : ''}${JSON.stringify(condition.header)} ${JSON.stringify(condition.keys[0])}`;

            case 'envelope':
                return `envelope ${condition.matchType} ${condition.addressPart || ':all'} ${condition.comparator !== 'i;ascii-casemap' ? `:comparator "${condition.comparator}" ` : ''}${JSON.stringify(condition.part)} ${JSON.stringify(condition.keys[0])}`;

            case 'size':
                return `size ${condition.operator} ${condition.size}`;

            case 'body':
                return `body ${condition.matchType} ${condition.transform ? `${condition.transform} ` : ''}${condition.comparator !== 'i;ascii-casemap' ? `:comparator "${condition.comparator}" ` : ''}${JSON.stringify(condition.keys[0])}`;

            case 'exists':
                return `exists ${JSON.stringify(condition.header)}`;

            case 'true':
                return 'true';

            case 'false':
                return 'false';

            default:
                return 'true';
        }
    }, []);

    // Helper to generate action code
    const generateActionCode = useCallback((action) => {
        switch (action.type) {
            case 'keep':
                return 'keep';

            case 'fileinto':
                const fiOptions = [];
                if (action.parameters?.create) fiOptions.push(':create');
                if (action.parameters?.flags && action.parameters.flags.length > 0)
                    fiOptions.push(`:flags ${JSON.stringify(action.parameters.flags)}`);
                if (action.parameters?.copy) fiOptions.push(':copy');

                return `fileinto ${fiOptions.join(' ')} ${JSON.stringify(action.parameters?.mailbox || 'INBOX')}`;

            case 'redirect':
                const redOptions = [];
                if (action.parameters?.copy) redOptions.push(':copy');

                return `redirect ${redOptions.join(' ')} ${JSON.stringify(action.parameters?.address || '')}`;

            case 'discard':
                return 'discard';

            case 'reject':
                return `reject ${JSON.stringify(action.parameters?.reason || '')}`;

            case 'addheader':
                return `addheader ${action.parameters?.last ? ':last ' : ''}${JSON.stringify(action.parameters?.name || '')} ${JSON.stringify(action.parameters?.value || '')}`;

            case 'deleteheader':
                const dhOptions = [];
                if (action.parameters?.index) dhOptions.push(`:index ${action.parameters.index}`);
                if (action.parameters?.last) dhOptions.push(':last');
                if (action.parameters?.matchType) dhOptions.push(action.parameters.matchType);
                if (action.parameters?.comparator !== 'i;ascii-casemap')
                    dhOptions.push(`:comparator "${action.parameters.comparator}"`);

                return `deleteheader ${dhOptions.join(' ')} ${JSON.stringify(action.parameters?.name || '')} ${action.parameters?.value ? JSON.stringify(action.parameters.value) : ''}`;

            case 'stop':
                return 'stop';

            case 'vacation':
                const vacOptions = [];
                if (action.parameters?.days) vacOptions.push(`:days ${action.parameters.days}`);
                if (action.parameters?.subject) vacOptions.push(`:subject ${JSON.stringify(action.parameters.subject)}`);

                return `vacation ${vacOptions.join(' ')} ${JSON.stringify(action.parameters?.message || '')}`;

            default:
                return '';
        }
    }, []);

    // Toggle code editing mode
    const toggleCodeEditMode = useCallback(() => {
        if (!codeEditMode) {
            // Entering edit mode
            setEditedCode(generatedCode);
            setCodeEditMode(true);
        } else {
            // Exiting edit mode - parse the edited code
            try {
                const parsedScript = parseSieveScript(editedCode);
                if (parsedScript && parsedScript.rules) {
                    setRules(parsedScript.rules);
                    if (parsedScript.extensions) {
                        setExtensions(prev => ({
                            ...prev,
                            ...parsedScript.extensions
                        }));
                    }
                    setCodeEditMode(false);
                    showNotification("Script updated from code", "success");
                } else {
                    if (!window.confirm("Unable to fully parse the script. Exit edit mode anyway?")) {
                        return;
                    }
                    setCodeEditMode(false);
                }
            } catch (e) {
                console.error("Error parsing edited code:", e);
                if (window.confirm("Error parsing code. Discard changes and exit edit mode?")) {
                    setCodeEditMode(false);
                }
            }
        }
    }, [codeEditMode, generatedCode, editedCode, showNotification]);

    // Copy generated code to clipboard
    const copyToClipboard = useCallback(() => {
        const codeToCopy = codeEditMode ? editedCode : generatedCode;
        navigator.clipboard.writeText(codeToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                showNotification("Code copied to clipboard", "success");
            })
            .catch(err => {
                console.error('Error copying to clipboard:', err);
                showNotification("Failed to copy to clipboard", "error");
            });
    }, [generatedCode, editedCode, codeEditMode, showNotification]);

    // Download generated code as a .sieve file
    const downloadCode = useCallback(() => {
        const filename = 'filter.sieve';
        const codeToDownload = codeEditMode ? editedCode : generatedCode;
        const blob = new Blob([codeToDownload], {type: 'text/plain'});

        // Create an object URL for the blob
        const url = URL.createObjectURL(blob);

        // Update the download link
        if (downloadLinkRef.current) {
            downloadLinkRef.current.href = url;
            downloadLinkRef.current.download = filename;
            downloadLinkRef.current.click();

            // Clean up the URL
            setTimeout(() => URL.revokeObjectURL(url), 100);

            showNotification(`Downloaded as ${filename}`, "success");
        }
    }, [generatedCode, editedCode, codeEditMode, showNotification]);

    // Import code from file
    const importCode = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            try {
                // Try to parse the Sieve script
                const result = parseSieveScript(content);

                if (result && result.rules && result.rules.length > 0) {
                    if (window.confirm("Import this script? This will replace your current script.")) {
                        setRules(result.rules);

                        if (result.extensions) {
                            setExtensions(prev => ({
                                ...prev,
                                ...result.extensions
                            }));
                        }

                        showNotification("Script imported successfully", "success");
                    }
                } else {
                    showNotification("Could not parse the script file. Please check the format.", "error");
                }
            } catch (error) {
                console.error('Error parsing Sieve code:', error);
                showNotification("Failed to parse the Sieve code", "error");
            }

            // Reset the file input
            if (importFileRef.current) {
                importFileRef.current.value = "";
            }
        };
        reader.readAsText(file);
    }, [showNotification]);

    // Create a new script
    const newScript = useCallback(() => {
        if (rules.length > 0) {
            if (window.confirm("Create a new script? This will clear your current script.")) {
                setRules([]);
                addRule();
                showNotification("Created new script", "success");
            }
        } else {
            addRule();
            showNotification("Created new script", "success");
        }
    }, [rules, addRule, showNotification]);

    // Handle code editor changes
    const handleCodeChange = useCallback((e) => {
        setEditedCode(e.target.value);
    }, []);

    // Load a template
    const loadTemplate = useCallback((templateKey) => {
        const template = SCRIPT_TEMPLATES[templateKey];
        if (template) {
            if (rules.length > 0) {
                if (!window.confirm("This will replace your current script. Continue?")) {
                    return;
                }
            }

            // Deep copy the template rules
            const newRules = JSON.parse(JSON.stringify(template.rules));

            // Initialize expanded state for all rules
            const expandedState = {};
            const initExpandState = (ruleList) => {
                ruleList.forEach(rule => {
                    expandedState[rule.id] = true;
                    if (rule.subrules?.length) {
                        initExpandState(rule.subrules);
                    }
                });
            };
            initExpandState(newRules);

            setExpandedRules(expandedState);
            setRules(newRules);

            if (template.extensions) {
                setExtensions(prev => ({
                    ...prev,
                    ...template.extensions
                }));
            }

            setShowTemplates(false);
            showNotification(`Loaded "${template.name}" template`, "success");
        }
    }, [rules, showNotification]);

    // Toggle a category's expanded state
    const toggleCategory = useCallback((category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    }, []);

    // Recursive rule rendering component
    const RuleComponent = useCallback(({
                                           rule,
                                           ruleIndex,
                                           parentId = null,
                                           depth = 0,
                                           isLastRule = false
                                       }) => {
        const isExpanded = expandedRules[rule.id];
        const hasSubrules = rule.subrules && rule.subrules.length > 0;
        const indentClass = depth > 0 ? `ml-${Math.min(depth * 4, 16)}` : '';
        const borderClass = depth > 0 ? 'border-l-2 border-blue-200 pl-4' : '';

        return (
            <div
                key={rule.id}
                className={`${indentClass} ${rule.active ? '' : 'opacity-60'}`}
            >
                <div className={`border rounded-lg p-4 ${rule.active ? 'border-blue-400' : 'border-gray-300 bg-gray-50'} ${borderClass} mb-4`}>
                    <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                        <div className="flex items-center">
                            <button
                                onClick={() => toggleRuleActive(rule.id)}
                                className="mr-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                                title={rule.active ? "Disable rule" : "Enable rule"}
                                aria-label={rule.active ? "Disable rule" : "Enable rule"}
                            >
                                {rule.active ? (
                                    <CheckSquare className="h-5 w-5 text-blue-500"/>
                                ) : (
                                    <Square className="h-5 w-5"/>
                                )}
                            </button>

                            {/* Rule title with optional icon for nested rules */}
                            <div className="flex items-center">
                                {depth > 0 && (
                                    <Layers className="h-4 w-4 mr-1 text-blue-500" />
                                )}
                                <h3 className="text-lg font-medium">
                                    {depth === 0 ? `Rule ${ruleIndex + 1}` : `Nested Rule ${ruleIndex + 1}`}
                                </h3>
                            </div>

                            {!rule.active && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                                    Disabled
                                </span>
                            )}
                        </div>

                        <div className="flex space-x-1">
                            {/* Rule controls */}
                            {depth === 0 && (
                                <>
                                    <button
                                        onClick={() => moveRuleUp(ruleIndex)}
                                        disabled={ruleIndex === 0}
                                        className={`p-1 rounded ${ruleIndex === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                        title="Move rule up"
                                        aria-label="Move rule up"
                                    >
                                        <ArrowUp className="h-4 w-4"/>
                                    </button>
                                    <button
                                        onClick={() => moveRuleDown(ruleIndex)}
                                        disabled={isLastRule}
                                        className={`p-1 rounded ${isLastRule ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                        title="Move rule down"
                                        aria-label="Move rule down"
                                    >
                                        <ArrowDown className="h-4 w-4"/>
                                    </button>
                                </>
                            )}

                            {/* Add subrule button */}
                            <button
                                onClick={() => addSubrule(rule.id)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded flex items-center"
                                title="Add nested rule"
                                aria-label="Add nested rule"
                            >
                                <PlusSquare className="h-4 w-4"/>
                            </button>

                            <button
                                onClick={() => duplicateRule(rule.id, parentId)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Duplicate rule"
                                aria-label="Duplicate rule"
                            >
                                <Copy className="h-4 w-4"/>
                            </button>

                            <button
                                onClick={() => removeRule(rule.id, parentId)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Delete rule"
                                aria-label="Delete rule"
                            >
                                <Trash2 className="h-4 w-4"/>
                            </button>

                            {/* Expand/collapse button for rules with subrules */}
                            {hasSubrules && (
                                <button
                                    onClick={() => toggleRuleExpanded(rule.id)}
                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                    title={isExpanded ? "Collapse nested rules" : "Expand nested rules"}
                                    aria-label={isExpanded ? "Collapse" : "Expand"}
                                >
                                    {isExpanded ?
                                        <ChevronDown className="h-4 w-4"/> :
                                        <ChevronRight className="h-4 w-4"/>
                                    }
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Conditions Section */}
                    <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center">
                                <h4 className="font-medium">If {rule.conditions.length > 1 && (
                                    <button
                                        onClick={() => toggleCombinator(rule.id)}
                                        className="text-blue-600 text-sm underline"
                                    >
                                        {rule.combinator === 'anyof' ? 'any' : 'all'} of the
                                        following conditions
                                    </button>
                                )}</h4>

                                {rule.conditions.length > 1 && (
                                    <HelpTooltip
                                        text={rule.combinator === 'anyof' ?
                                            "If ANY of the conditions match, the rule will be applied" :
                                            "ALL conditions must match for the rule to be applied"}
                                    >
                                        <button
                                            aria-label="Combinator information"
                                            className="ml-1 text-gray-400 hover:text-blue-500"
                                        >
                                            <HelpCircle className="h-4 w-4"/>
                                        </button>
                                    </HelpTooltip>
                                )}
                            </div>
                            <button
                                onClick={() => addCondition(rule.id)}
                                className="text-sm px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded flex items-center"
                                aria-label="Add condition"
                            >
                                <Plus className="h-3 w-3 mr-1"/>
                                Add Condition
                            </button>
                        </div>

                        <div className="space-y-2">
                            {rule.conditions.map((condition, condIndex) => (
                                <div
                                    key={condIndex}
                                    className="flex items-start space-x-2 bg-gray-50 p-2 rounded"
                                >
                                    <ConditionBuilder
                                        condition={condition}
                                        onChange={(updates) => updateCondition(rule.id, condIndex, updates)}
                                        extensions={extensions}
                                    />
                                    <button
                                        onClick={() => removeCondition(rule.id, condIndex)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded mt-1"
                                        aria-label="Remove condition"
                                    >
                                        <X className="h-4 w-4"/>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="font-medium">Then</h4>
                            <button
                                onClick={() => addAction(rule.id)}
                                className="text-sm px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded flex items-center"
                                aria-label="Add action"
                            >
                                <Plus className="h-3 w-3 mr-1"/>
                                Add Action
                            </button>
                        </div>

                        <div className="space-y-2">
                            {rule.actions.map((action, actionIndex) => (
                                <div
                                    key={actionIndex}
                                    className="flex items-start space-x-2 bg-gray-50 p-2 rounded"
                                >
                                    <ActionBuilder
                                        action={action}
                                        onChange={(updates) => updateAction(rule.id, actionIndex, updates)}
                                        extensions={extensions}
                                    />
                                    <button
                                        onClick={() => removeAction(rule.id, actionIndex)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded mt-1"
                                        aria-label="Remove action"
                                    >
                                        <X className="h-4 w-4"/>
                                    </button>
                                </div>
                            ))}

                            {rule.actions.length === 0 && !hasSubrules && (
                                <div className="text-gray-500 text-sm italic p-2">
                                    No actions defined. Add an action or create nested rules.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subrules section */}
                    {hasSubrules && isExpanded && (
                        <div className="mt-4 pl-2">
                            <div className="flex items-center mb-2 text-blue-600">
                                <Layers className="h-4 w-4 mr-1" />
                                <h4 className="font-medium">Nested Rules</h4>
                            </div>
                            <div className="space-y-2">
                                {rule.subrules.map((subrule, subruleIndex) => (
                                    <RuleComponent
                                        key={subrule.id}
                                        rule={subrule}
                                        ruleIndex={subruleIndex}
                                        parentId={rule.id}
                                        depth={depth + 1}
                                        isLastRule={subruleIndex === rule.subrules.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }, [
        expandedRules, toggleRuleActive, moveRuleUp, moveRuleDown, addSubrule,
        duplicateRule, removeRule, toggleRuleExpanded, toggleCombinator,
        addCondition, updateCondition, removeCondition, addAction,
        updateAction, removeAction, extensions
    ]);

    // Notification component (rendered inline)
    const NotificationComponent = useMemo(() => {
        if (!notification) return null;

        const bgColor = notification.type === 'error' ? 'bg-red-100 border-red-400 text-red-800' :
            notification.type === 'success' ? 'bg-green-100 border-green-400 text-green-800' :
                'bg-blue-100 border-blue-400 text-blue-800';

        const icon = notification.type === 'error' ? <AlertTriangle className="h-5 w-5 mr-2"/> :
            notification.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2"/> :
                <Info className="h-5 w-5 mr-2"/>;

        return (
            <div
                className={`fixed bottom-4 right-4 px-4 py-3 rounded border ${bgColor} flex items-center shadow-lg z-50`}>
                {icon}
                <span>{notification.message}</span>
                <button
                    onClick={() => setNotification(null)}
                    className="ml-4 text-gray-600 hover:text-gray-800"
                >
                    <X className="h-4 w-4"/>
                </button>
            </div>
        );
    }, [notification]);

    // Main editor render
    return (
        <div className="w-full max-w-6xl mx-auto p-4">
            {NotificationComponent}

            <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center">
                        <h1 className="text-2xl font-bold text-gray-800">Sieve Script Editor</h1>
                        <button
                            onClick={() => setShowHelp(!showHelp)}
                            className="ml-2 text-gray-500 hover:text-blue-600"
                            aria-label="Help"
                            title="Show help information"
                        >
                            <HelpCircle className="h-5 w-5"/>
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={newScript}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center"
                            title="Create a new script"
                        >
                            <FileText className="mr-1 h-4 w-4"/>
                            New
                        </button>

                        <button
                            onClick={() => setShowTemplates(true)}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center"
                            title="Load a template"
                        >
                            <Copy className="mr-1 h-4 w-4"/>
                            Templates
                        </button>

                        <label
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center cursor-pointer">
                            <input
                                ref={importFileRef}
                                type="file"
                                accept=".sieve,.txt"
                                onChange={importCode}
                                className="hidden"
                            />
                            <Upload className="mr-1 h-4 w-4"/>
                            Import
                        </label>

                        <button
                            onClick={() => setShowCode(!showCode)}
                            className={`px-3 py-2 ${showCode ? 'bg-blue-700' : 'bg-blue-600'} hover:bg-blue-700 text-white rounded flex items-center`}
                            title={showCode ? "Hide generated code" : "Show generated code"}
                        >
                            <Code className="mr-1 h-4 w-4"/>
                            {showCode ? 'Hide Code' : 'Show Code'}
                        </button>

                        <button
                            onClick={copyToClipboard}
                            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center relative"
                            title="Copy code to clipboard"
                        >
                            {copied ? <CopyCheck className="mr-1 h-4 w-4"/> : <Clipboard className="mr-1 h-4 w-4"/>}
                            {copied ? 'Copied!' : 'Copy'}
                        </button>

                        <button
                            onClick={downloadCode}
                            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center"
                            title="Download as .sieve file"
                        >
                            <Download className="mr-1 h-4 w-4"/>
                            Download
                        </button>
                        <a ref={downloadLinkRef} className="hidden"></a>
                    </div>
                </div>

                {showHelp && (
                    <div className="mt-4 bg-blue-50 p-4 rounded text-sm">
                        <h2 className="font-bold text-blue-800 mb-2">How to use this editor:</h2>
                        <ol className="list-decimal pl-5 space-y-1 text-blue-900">
                            <li>Create filtering rules by adding conditions (what to match) and actions (what to do)</li>
                            <li>Create nested rules to implement complex logic like "if from this domain, then check these conditions"</li>
                            <li>Enable required extensions in the sidebar when using specific features</li>
                            <li>Use the "Show Code" button to see the generated Sieve script</li>
                            <li>You can directly edit the code and update the GUI from your changes</li>
                            <li>Duplicate rules to quickly create similar filters</li>
                            <li>Download your script for use with any Sieve-compatible mail server</li>
                        </ol>
                        <p className="mt-2 text-blue-800">Your script is automatically saved in your browser as you work.</p>
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={() => setShowHelp(false)}
                                className="px-3 py-1 bg-blue-200 text-blue-800 rounded hover:bg-blue-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Validation warnings */}
            {validationWarnings.length > 0 && !codeEditMode && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0"/>
                        <div>
                            <h3 className="font-medium text-yellow-800">Script Warnings</h3>
                            <ul className="mt-1 text-sm text-yellow-700 list-disc pl-5">
                                {validationWarnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Templates modal */}
            {showTemplates && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Script Templates</h2>
                            <button
                                onClick={() => setShowTemplates(false)}
                                className="text-gray-500 hover:text-gray-700"
                                aria-label="Close templates dialog"
                            >
                                <X className="h-5 w-5"/>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                            {Object.entries(SCRIPT_TEMPLATES).map(([key, template]) => (
                                <button
                                    key={key}
                                    onClick={() => loadTemplate(key)}
                                    className="text-left p-3 border rounded hover:bg-gray-50 flex justify-between items-center"
                                >
                                    <span className="font-medium">{template.name}</span>
                                    <ChevronRight className="h-4 w-4 text-gray-400"/>
                                </button>
                            ))}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowTemplates(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!codeEditMode && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
                    <div className="lg:col-span-3">
                        {/* Rules section */}
                        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center">
                                    <h2 className="text-xl font-semibold">Rules</h2>
                                    <HelpTooltip text="A rule defines what to look for in emails (conditions) and what to do when matched (actions). Rules can contain nested rules for complex logic.">
                                        <button
                                            aria-label="Information about rules"
                                            className="ml-1 text-gray-400 hover:text-blue-500"
                                        >
                                            <HelpCircle className="h-4 w-4"/>
                                        </button>
                                    </HelpTooltip>
                                </div>
                                <button
                                    onClick={addRule}
                                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center text-sm"
                                    aria-label="Add rule"
                                >
                                    <Plus className="mr-1 h-4 w-4"/>
                                    Add Rule
                                </button>
                            </div>

                            {rules.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                    <div className="flex flex-col items-center">
                                        <AlertCircle className="h-12 w-12 text-gray-400 mb-2"/>
                                        <h3 className="text-lg font-medium text-gray-600 mb-1">No Rules Defined</h3>
                                        <p className="text-gray-500 max-w-md mb-4">Create a rule to start building your email filtering script.</p>
                                        <button
                                            onClick={addRule}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center"
                                        >
                                            <Plus className="mr-1 h-4 w-4"/>
                                            Add First Rule
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {rules.map((rule, index) => (
                                        <RuleComponent
                                            key={rule.id}
                                            rule={rule}
                                            ruleIndex={index}
                                            isLastRule={index === rules.length - 1}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="mt-4 flex justify-center">
                                <button
                                    onClick={addRule}
                                    className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"
                                    aria-label="Add rule"
                                >
                                    <Plus className="mr-1 h-4 w-4"/>
                                    Add Rule
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* The Extensions sidebar would go here (same as original) */}
                    <div className="lg:col-span-1">
                        {/* Extensions section - same as original code */}
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold">Extensions</h2>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setExpandedCategories({
                                            core: true,
                                            actions: true,
                                            tests: true,
                                            message: true,
                                            advanced: true
                                        })}
                                        className="text-sm text-blue-600 hover:text-blue-800"
                                        title="Expand all categories"
                                    >
                                        Expand All
                                    </button>
                                    <button
                                        onClick={() => setExpandedCategories({
                                            core: false,
                                            actions: false,
                                            tests: false,
                                            message: false,
                                            advanced: false
                                        })}
                                        className="text-sm text-blue-600 hover:text-blue-800"
                                        title="Collapse all categories"
                                    >
                                        Collapse All
                                    </button>
                                </div>
                            </div>

                            {/* Extension toggles would go here */}
                            <div className="space-y-4">
                                {/* Core extensions toggle section */}
                                <div className="border rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => toggleCategory('core')}
                                        className="w-full px-3 py-2 bg-gray-100 flex justify-between items-center font-medium"
                                        aria-expanded={expandedCategories.core}
                                    >
                                        Core Extensions
                                        {expandedCategories.core ? <ChevronDown className="h-4 w-4"/> :
                                            <ChevronRight className="h-4 w-4"/>}
                                    </button>

                                    {expandedCategories.core && (
                                        <div className="p-3 space-y-2">
                                            {/* Extension toggles - core */}
                                            <div className="flex items-center group">
                                                <input
                                                    type="checkbox"
                                                    id="ext-fileinto"
                                                    checked={extensions.fileinto}
                                                    onChange={() => toggleExtension('fileinto')}
                                                    className="mr-2 h-4 w-4"
                                                    aria-label="Enable fileinto extension"
                                                />
                                                <label htmlFor="ext-fileinto" className="flex-1">fileinto</label>
                                                <HelpTooltip text="Adds the fileinto action for delivering to specific mailboxes" position="left">
                                                    <button
                                                        className="text-gray-400 hover:text-blue-600"
                                                        aria-label="Information about fileinto extension"
                                                    >
                                                        <Info className="h-4 w-4"/>
                                                    </button>
                                                </HelpTooltip>
                                            </div>

                                            <div className="flex items-center group">
                                                <input
                                                    type="checkbox"
                                                    id="ext-envelope"
                                                    checked={extensions.envelope}
                                                    onChange={() => toggleExtension('envelope')}
                                                    className="mr-2 h-4 w-4"
                                                    aria-label="Enable envelope extension"
                                                />
                                                <label htmlFor="ext-envelope" className="flex-1">envelope</label>
                                                <HelpTooltip text="Adds the envelope test for accessing SMTP envelope information" position="left">
                                                    <button
                                                        className="text-gray-400 hover:text-blue-600"
                                                        aria-label="Information about envelope extension"
                                                    >
                                                        <Info className="h-4 w-4"/>
                                                    </button>
                                                </HelpTooltip>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* More extension categories would go here */}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Code view */}
            {showCode && (
                <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-xl font-semibold">
                            {codeEditMode ? 'Edit Sieve Code' : 'Generated Sieve Code'}
                        </h2>
                        <div className="flex space-x-2">
                            <button
                                onClick={toggleCodeEditMode}
                                className={`px-2 py-1 text-sm ${codeEditMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} rounded flex items-center`}
                                title={codeEditMode ? "Apply changes and exit edit mode" : "Edit code directly"}
                            >
                                {codeEditMode ? (
                                    <>
                                        <PlayCircle className="mr-1 h-4 w-4"/>
                                        Apply Changes
                                    </>
                                ) : (
                                    <>
                                        <Edit className="mr-1 h-4 w-4"/>
                                        Edit Code
                                    </>
                                )}
                            </button>

                            <button
                                onClick={copyToClipboard}
                                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center"
                                title="Copy code to clipboard"
                            >
                                <Copy className="mr-1 h-4 w-4"/>
                                Copy
                            </button>

                            <button
                                onClick={downloadCode}
                                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center"
                                title="Download as .sieve file"
                            >
                                <Download className="mr-1 h-4 w-4"/>
                                Download
                            </button>
                        </div>
                    </div>

                    {codeEditMode ? (
                        <div className="bg-gray-100 rounded border border-gray-300">
                            <textarea
                                ref={codeEditorRef}
                                value={editedCode}
                                onChange={handleCodeChange}
                                className="w-full h-96 p-4 font-mono text-sm bg-transparent resize-none focus:outline-none"
                                spellCheck="false"
                            />
                        </div>
                    ) : (
                        <pre className="bg-gray-100 p-4 rounded overflow-x-auto whitespace-pre text-sm">
                            {generatedCode || '# No code generated yet'}
                        </pre>
                    )}

                    {codeEditMode && (
                        <div className="mt-3 flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setEditedCode(generatedCode);
                                    showNotification("Changes discarded", "info");
                                }}
                                className="px-3 py-1 bg-gray-200 text-gray-800 rounded flex items-center"
                            >
                                <RotateCcw className="mr-1 h-4 w-4"/>
                                Reset
                            </button>
                            <button
                                onClick={toggleCodeEditMode}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded flex items-center"
                            >
                                <PlayCircle className="mr-1 h-4 w-4"/>
                                Apply Changes
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Component to build a condition
const ConditionBuilder = React.memo(function ConditionBuilder({condition, onChange, extensions}) {
    // Get help text for current condition type
    const conditionTypeHelp = condition.type === 'header' ?
        "Checks if a header (like Subject, From, To) contains, matches, or equals specific text" :
        condition.type === 'address' ?
            "Tests parts of email addresses (username, domain, or complete address)" :
            condition.type === 'envelope' ?
                "Tests the message envelope (the SMTP sender/recipient, not what's in the From/To headers)" :
                condition.type === 'size' ?
                    "Checks if the message size is over or under a specified limit" :
                    condition.type === 'body' ?
                        "Examines the message body for specific content" :
                        condition.type === 'exists' ?
                            "Checks if a header exists in the message" :
                            condition.type === 'true' ?
                                "Always matches (useful for creating default rules)" :
                                condition.type === 'false' ?
                                    "Never matches (useful for testing)" :
                                    "Configure what to check in this condition";

    const handleTypeChange = (newType) => {
        let newCondition = {type: newType};

        // Set default fields based on type
        switch (newType) {
            case 'header':
                newCondition = {
                    ...newCondition,
                    comparator: 'i;ascii-casemap',
                    matchType: ':is',
                    header: 'Subject',
                    keys: ['']
                };
                break;

            case 'address':
                newCondition = {
                    ...newCondition,
                    comparator: 'i;ascii-casemap',
                    matchType: ':is',
                    addressPart: ':all',
                    header: 'From',
                    keys: ['']
                };
                break;

            case 'envelope':
                newCondition = {
                    ...newCondition,
                    comparator: 'i;ascii-casemap',
                    matchType: ':is',
                    addressPart: ':all',
                    part: 'from',
                    keys: ['']
                };
                break;

            case 'size':
                newCondition = {
                    ...newCondition,
                    operator: ':over',
                    size: '100K'
                };
                break;

            case 'body':
                newCondition = {
                    ...newCondition,
                    comparator: 'i;ascii-casemap',
                    matchType: ':contains',
                    transform: ':text',
                    keys: ['']
                };
                break;

            case 'exists':
                newCondition = {
                    ...newCondition,
                    header: ['Subject']
                };
                break;

            default:
                break;
        }

        onChange(newCondition);
    };

    return (
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <div className="flex items-center col-span-1">
                <select
                    value={condition.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    className="px-2 py-1 border rounded text-sm w-full"
                    aria-label="Condition type"
                >
                    <option value="header">Header</option>
                    <option value="address">Address</option>
                    {extensions.envelope && <option value="envelope">Envelope</option>}
                    <option value="size">Size</option>
                    {extensions.body && <option value="body">Body</option>}
                    <option value="exists">Exists</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                </select>
                <HelpTooltip text={conditionTypeHelp} position="right">
                    <button aria-label="Condition type help" className="ml-1 text-gray-400 hover:text-blue-500">
                        <HelpCircle className="h-4 w-4"/>
                    </button>
                </HelpTooltip>
            </div>

            {condition.type === 'header' && (
                <>
                    <div className="flex items-center col-span-1">
                        <select
                            value={condition.matchType}
                            onChange={(e) => onChange({matchType: e.target.value})}
                            className="px-2 py-1 border rounded text-sm w-full"
                            aria-label="Match type"
                        >
                            <option value=":is">is</option>
                            <option value=":contains">contains</option>
                            <option value=":matches">matches</option>
                        </select>
                        <HelpTooltip
                            text={condition.matchType === ':is' ?
                                "Exact match - the entire content must be identical" :
                                condition.matchType === ':contains' ?
                                    "Partial match - the content must contain this text" :
                                    "Pattern match using wildcards (* for any characters, ? for any single character)"}
                            position="right"
                        >
                            <button aria-label="Match type help" className="ml-1 text-gray-400 hover:text-blue-500">
                                <HelpCircle className="h-4 w-4"/>
                            </button>
                        </HelpTooltip>
                    </div>

                    <input
                        type="text"
                        value={condition.header}
                        onChange={(e) => onChange({header: e.target.value})}
                        placeholder="Header name"
                        className="px-2 py-1 border rounded text-sm col-span-1"
                        aria-label="Header name"
                    />

                    <input
                        type="text"
                        value={Array.isArray(condition.keys) ? condition.keys[0] : condition.keys || ''}
                        onChange={(e) => onChange({keys: [e.target.value]})}
                        placeholder="Value"
                        className="px-2 py-1 border rounded text-sm col-span-2"
                        aria-label="Header value"
                    />
                </>
            )}

            {condition.type === 'address' && (
                <>
                    <div className="flex items-center col-span-1">
                        <select
                            value={condition.matchType}
                            onChange={(e) => onChange({matchType: e.target.value})}
                            className="px-2 py-1 border rounded text-sm w-full"
                            aria-label="Match type"
                        >
                            <option value=":is">is</option>
                            <option value=":contains">contains</option>
                            <option value=":matches">matches</option>
                        </select>
                        <HelpTooltip
                            text={condition.matchType === ':is' ?
                                "Exact match - the entire content must be identical" :
                                condition.matchType === ':contains' ?
                                    "Partial match - the content must contain this text" :
                                    "Pattern match using wildcards (* for any characters, ? for any single character)"}
                            position="right"
                        >
                            <button aria-label="Match type help" className="ml-1 text-gray-400 hover:text-blue-500">
                                <HelpCircle className="h-4 w-4"/>
                            </button>
                        </HelpTooltip>
                    </div>

                    <div className="flex items-center col-span-1">
                        <select
                            value={condition.addressPart}
                            onChange={(e) => onChange({addressPart: e.target.value})}
                            className="px-2 py-1 border rounded text-sm w-full"
                            aria-label="Address part"
                        >
                            <option value=":all">all</option>
                            <option value=":localpart">local part</option>
                            <option value=":domain">domain</option>
                        </select>
                        <HelpTooltip
                            text={condition.addressPart === ':all' ?
                                "The entire email address (e.g., user@example.com)" :
                                condition.addressPart === ':localpart' ?
                                    "Just the username part (e.g., 'user' from user@example.com)" :
                                    "Just the domain part (e.g., 'example.com' from user@example.com)"}
                            position="right"
                        >
                            <button aria-label="Address part help" className="ml-1 text-gray-400 hover:text-blue-500">
                                <HelpCircle className="h-4 w-4"/>
                            </button>
                        </HelpTooltip>
                    </div>

                    <input
                        type="text"
                        value={condition.header}
                        onChange={(e) => onChange({header: e.target.value})}
                        placeholder="Header (From, To, Cc)"
                        className="px-2 py-1 border rounded text-sm col-span-1"
                        aria-label="Address header"
                    />

                    <input
                        type="text"
                        value={Array.isArray(condition.keys) ? condition.keys[0] : condition.keys || ''}
                        onChange={(e) => onChange({keys: [e.target.value]})}
                        placeholder="Address"
                        className="px-2 py-1 border rounded text-sm col-span-1"
                        aria-label="Address value"
                    />
                </>
            )}

            {/* Other condition types rendering would be here */}
        </div>
    );
});

// Component to build an action - simplified version
const ActionBuilder = React.memo(function ActionBuilder({action, onChange, extensions}) {
    // Helper to update parameters
    const updateParameters = (updates) => {
        onChange({
            parameters: {
                ...action.parameters,
                ...updates
            }
        });
    };

    return (
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <div className="flex items-center col-span-1">
                <select
                    value={action.type}
                    onChange={(e) => {
                        const newType = e.target.value;
                        let newAction = {type: newType};

                        // Set default parameters based on action type
                        switch (newType) {
                            case 'fileinto':
                                newAction.parameters = {mailbox: 'INBOX.Filtered'};
                                break;
                            case 'redirect':
                                newAction.parameters = {address: 'user@example.com'};
                                break;
                            // Other action types would have their defaults here
                        }

                        onChange(newAction);
                    }}
                    className="px-2 py-1 border rounded text-sm w-full"
                    aria-label="Action type"
                >
                    <option value="keep">Keep</option>
                    {extensions.fileinto && <option value="fileinto">File Into</option>}
                    <option value="redirect">Redirect</option>
                    <option value="discard">Discard</option>
                    {extensions.reject && <option value="reject">Reject</option>}
                    {extensions.editheader && <option value="addheader">Add Header</option>}
                    {extensions.editheader && <option value="deleteheader">Delete Header</option>}
                    <option value="stop">Stop</option>
                    {extensions.vacation && <option value="vacation">Vacation</option>}
                </select>
                <HelpTooltip text={
                    action.type === 'keep' ?
                        "Keep the message in the inbox (this is the default if no other action is specified)" :
                        action.type === 'fileinto' ?
                            "Move the message to a specific mailbox/folder" :
                            action.type === 'redirect' ?
                                "Forward the message to another email address" :
                                action.type === 'discard' ?
                                    "Silently delete the message without notification" :
                                    action.type === 'reject' ?
                                        "Reject the message with an explanation returned to the sender" :
                                        action.type === 'addheader' ?
                                            "Add a new header to the message" :
                                            action.type === 'deleteheader' ?
                                                "Remove a header from the message" :
                                                action.type === 'stop' ?
                                                    "Stop processing this message with other rules" :
                                                    action.type === 'vacation' ?
                                                        "Send an automatic reply to the sender" :
                                                        "Configure what happens when this rule matches"
                } position="right">
                    <button aria-label="Action type help" className="ml-1 text-gray-400 hover:text-blue-500">
                        <HelpCircle className="h-4 w-4"/>
                    </button>
                </HelpTooltip>
            </div>

            {action.type === 'fileinto' && (
                <>
                    <div className="flex items-center col-span-2">
                        <input
                            type="text"
                            value={action.parameters?.mailbox || ''}
                            onChange={(e) => updateParameters({mailbox: e.target.value})}
                            placeholder="Mailbox"
                            className="px-2 py-1 border rounded text-sm w-full"
                            aria-label="Destination mailbox"
                        />
                        <HelpTooltip text="The folder where matching messages will be delivered">
                            <button aria-label="Mailbox information" className="ml-1 text-gray-400 hover:text-blue-500">
                                <HelpCircle className="h-4 w-4"/>
                            </button>
                        </HelpTooltip>
                    </div>

                    {extensions.copy && (
                        <div className="flex items-center col-span-1">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={action.parameters?.copy || false}
                                    onChange={(e) => updateParameters({copy: e.target.checked})}
                                    className="mr-1"
                                    aria-label="Keep copy in inbox"
                                />
                                <span className="text-sm">Copy</span>
                            </label>
                            <HelpTooltip text="Also keep a copy of the message in the inbox">
                                <button aria-label="Copy option information"
                                        className="ml-1 text-gray-400 hover:text-blue-500">
                                    <HelpCircle className="h-4 w-4"/>
                                </button>
                            </HelpTooltip>
                        </div>
                    )}
                </>
            )}

            {/* Other action types rendering would be here */}
        </div>
    );
});

// HelpTooltip component
const HelpTooltip = ({text, children, position = "top"}) => {
    const positionClasses = {
        top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
        bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
        left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
        right: "left-full top-1/2 transform -translate-y-1/2 ml-2"
    };

    return (
        <div className="relative inline-flex items-center group">
            {children}
            <div
                className={`absolute ${positionClasses[position]} w-64 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-50`}>
                {text}
            </div>
        </div>
    );
};

export default SieveEditor;